import {useEffect, useRef} from "react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {RequestState} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";

import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {usePrimaryClick} from "../../../hooks/use_primary_click";
import {IPropertyListObj} from "../../../property_list/hooks/use_property_list";
import {
    galleryButtonClick,
    moreInfoLinkClick,
    quickViewNextPropertyClick,
    quickViewPrevPropertyClick,
    quickViewPropertyChange
} from "../../../tracking/algolytics_hits/map_quick_view_sidebar_hit";
import {getAmplitudeViewItemEventProperties, IAmplitudeItemListContext} from "../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackAmplitudeButtonClick} from "../../../tracking/amplitude/track_amplitude_button_click";
import {gtmClickGalleryButton, gtmClickPropertyMoreInfo} from "../../../tracking/google_tag_manager/gtm_event_map_quick_view_sidebar";
import {getThumbnailLink} from "../../../utils/get_thumbnail_link";
import {QWActionButtons} from "../../components/quick_view/QWActionButtons";
import {QWHeaderWithPagination} from "../../components/quick_view/QWHeaderWithPagination";
import {GalleryType, QWImageWithButton} from "../../components/quick_view/QWImageWithButton";
import {QWLocalizationInfo} from "../../components/quick_view/QWLocalizationInfo";
import {QWPictograms} from "../../components/quick_view/QWPictograms";
import {QWPropertyInfoHeading} from "../../components/quick_view/QWPropertyInfoHeading";
import {createOfferListingAmplitudePendingDetailUrl} from "../../list/helpers/offer_listing_amplitude_item_list_id";
import {trackAmplitudeOpenGallery} from "../../tracking/amplitude/track_amplitude_open_gallery";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {IMapOfferSidebar} from "../types/MapOfferSidebar";

interface IProps {
    handleQuickViewClose: () => void;
    setSelectedIndex: (index: number) => void;
    propertyList: IPropertyListObj;
    selectedIndex: number;
    propertiesRequestState: RequestState;
    offer: IMapOfferSidebar;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
}

export const MapOfferSidebarQuickView = (props: IProps) => {
    const {handleQuickViewClose, selectedIndex, setSelectedIndex, propertiesRequestState, offer, amplitudeItemListContext} = props;
    const {properties, requestState: listRequestState} = props.propertyList;
    const selectedPropertyIndex = selectedIndex > properties.length - 1 ? 0 : selectedIndex;
    const selectedProperty = properties[selectedPropertyIndex];
    const isLoading = propertiesRequestState === RequestState.Waiting || listRequestState === RequestState.Waiting || !selectedProperty;

    const trackedViewItemPropertyIdRef = useRef<number | null>(null);

    useEffect(() => {
        if (!selectedProperty?.id) {
            return;
        }

        quickViewPropertyChange(offer.id, selectedProperty.id);

        if (!amplitudeItemListContext?.itemListId || trackedViewItemPropertyIdRef.current === selectedProperty.id) {
            return;
        }

        trackedViewItemPropertyIdRef.current = selectedProperty.id;

        trackAmplitudeEvent("view_item", {
            ...getAmplitudeViewItemEventProperties({
                offer: selectedProperty.offer,
                property: {
                    id: selectedProperty.id,
                    number: selectedProperty.number,
                    area: selectedProperty.area,
                    rooms: selectedProperty.rooms,
                    floor: selectedProperty.floor,
                    price: selectedProperty.price
                },
                itemListId: amplitudeItemListContext?.itemListId,
                itemListName: amplitudeItemListContext?.itemListName
            }),
            location: "listing_map",
            index: selectedPropertyIndex
        });
    }, [selectedProperty, amplitudeItemListContext?.itemListId]);

    const updatePropertyDetailLinkWithPendingContext = (event: React.MouseEvent<HTMLElement>) => {
        const anchor = event.currentTarget as HTMLAnchorElement;

        anchor.href = createOfferListingAmplitudePendingDetailUrl({
            url: anchor.href,
            itemId: offer.id,
            propertyId: selectedProperty.id,
            itemListContext: amplitudeItemListContext
        });
    };

    const handleDetailsButtonClick = usePrimaryClick((event: React.MouseEvent<HTMLElement>) => {
        updatePropertyDetailLinkWithPendingContext(event);
        gtmClickPropertyMoreInfo();
        moreInfoLinkClick(offer.id, selectedProperty.id);
    });

    const handleGalleryButtonClick = (type: GalleryType) => {
        gtmClickGalleryButton(type);
        galleryButtonClick(type, offer.id, selectedProperty.id);

        trackAmplitudeOpenGallery({
            location: "listing_map",
            offerId: offer.id,
            propertyId: selectedProperty.id,
            galleryView: type === "photo" ? "grid" : "viewer",
            photoSection: type === "photo" ? "gallery" : "floor_plan"
        });
    };

    const nextClickAnalyticsEvent = () => {
        quickViewNextPropertyClick();
        trackAmplitudeButtonClick({
            buttonName: "rp_listing_map_apartment_next",
            buttonCategory: "listing_map",
            value: "next",
            itemId: offer.id,
            propertyId: selectedProperty.id
        });
    };

    const prevClickAnalyticsEvent = () => {
        quickViewPrevPropertyClick();
        trackAmplitudeButtonClick({
            buttonName: "rp_listing_map_apartment_prev",
            buttonCategory: "listing_map",
            value: "previous",
            itemId: offer.id,
            propertyId: selectedProperty.id
        });
    };

    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";

    const firstPlanPage = selectedProperty.plan_image_pages[0];
    const propertyPlanImage = getThumbnailLink(firstPlanPage?.image_url, 760, 428) ?? firstPlanPage?.image.p_img_760x428;
    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerId: offer.id,
        offerSlug: offer.slug,
        propertyId: selectedProperty.id
    });

    return (
        <div className={quickViewContainer}>
            {isLoading ? (
                <div className={topLoaderContainer}>
                    <Loader fullWidth size="lg" />
                </div>
            ) : (
                <>
                    <QWHeaderWithPagination
                        handleQuickViewClose={handleQuickViewClose}
                        setSelectedIndex={setSelectedIndex}
                        propertyList={props.propertyList}
                        selectedIndex={selectedIndex}
                        onNextClickEvent={nextClickAnalyticsEvent}
                        onPrevClickEvent={prevClickAnalyticsEvent}
                    />

                    <QWImageWithButton
                        alt={offer.name}
                        firstImageSrc={propertyPlanImage}
                        secondImageSrc={getThumbnailLink(offer.main_image_url, 760, 428) ?? offer.main_image.m_img_760x428}
                        clickEvent={handleGalleryButtonClick}
                        onPropertyLinkClick={updatePropertyDetailLinkWithPendingContext}
                        propertyLink={propertyLink}
                    />

                    <QWPropertyInfoHeading rooms={selectedProperty.rooms} offerType={offer.type} offerName={offer.name} />
                </>
            )}

            <div className={bottomContainerPadding}>
                {/*Those two components are showing offer info so they don't need to be recompiled every time when property change */}
                <QWLocalizationInfo address={offer.address} distanceFromRegion={offer.stats?.distance_from_region} offerType={offer.type} />

                {isLoading ? (
                    <Loader fullWidth size="lg" />
                ) : (
                    <>
                        <QWPictograms selectedProperty={selectedProperty} />

                        <QWActionButtons
                            vendorId={offer.vendor.id}
                            offerId={offer.id}
                            propertyId={selectedProperty.id}
                            propertyPrice={selectedProperty.price ?? null}
                            propertyLink={propertyLink}
                            applicationSource={ApplicationSource.QuickViewPropertyModal}
                            applicationSourceSection={ApplicationSourceSection.SIDEBAR}
                            clickEvent={handleDetailsButtonClick}
                            hideApplicationButton={isForSaleOverLimit}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

const bottomContainerPadding = css({
    pt: 0,
    px: 2,
    pb: 4
});

const quickViewContainer = css({
    display: "flex",
    flexDirection: "column",
    position: "absolute",
    zIndex: 1,
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: "var(--colors-gray-100)",
    padding: 0
});

const topLoaderContainer = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "normal",
    height: "51rem"
});
import {useEffect, useRef} from "react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {RequestState} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";

import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {usePrimaryClick} from "../../../hooks/use_primary_click";
import {IPropertyListObj} from "../../../property_list/hooks/use_property_list";
import {
    galleryButtonClick,
    moreInfoLinkClick,
    quickViewNextPropertyClick,
    quickViewPrevPropertyClick,
    quickViewPropertyChange
} from "../../../tracking/algolytics_hits/map_quick_view_sidebar_hit";
import {getAmplitudeViewItemEventProperties, IAmplitudeItemListContext} from "../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackAmplitudeButtonClick} from "../../../tracking/amplitude/track_amplitude_button_click";
import {gtmClickGalleryButton, gtmClickPropertyMoreInfo} from "../../../tracking/google_tag_manager/gtm_event_map_quick_view_sidebar";
import {getThumbnailLink} from "../../../utils/get_thumbnail_link";
import {QWActionButtons} from "../../components/quick_view/QWActionButtons";
import {QWHeaderWithPagination} from "../../components/quick_view/QWHeaderWithPagination";
import {GalleryType, QWImageWithButton} from "../../components/quick_view/QWImageWithButton";
import {QWLocalizationInfo} from "../../components/quick_view/QWLocalizationInfo";
import {QWPictograms} from "../../components/quick_view/QWPictograms";
import {QWPropertyInfoHeading} from "../../components/quick_view/QWPropertyInfoHeading";
import {createOfferListingAmplitudePendingDetailUrl} from "../../list/helpers/offer_listing_amplitude_item_list_id";
import {trackAmplitudeOpenGallery} from "../../tracking/amplitude/track_amplitude_open_gallery";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {IMapOfferSidebar} from "../types/MapOfferSidebar";

interface IProps {
    handleQuickViewClose: () => void;
    setSelectedIndex: (index: number) => void;
    propertyList: IPropertyListObj;
    selectedIndex: number;
    propertiesRequestState: RequestState;
    offer: IMapOfferSidebar;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
}

export const MapOfferSidebarQuickView = (props: IProps) => {
    const {handleQuickViewClose, selectedIndex, setSelectedIndex, propertiesRequestState, offer, amplitudeItemListContext} = props;
    const {properties, requestState: listRequestState} = props.propertyList;
    const selectedPropertyIndex = selectedIndex > properties.length - 1 ? 0 : selectedIndex;
    const selectedProperty = properties[selectedPropertyIndex];
    const isLoading = propertiesRequestState === RequestState.Waiting || listRequestState === RequestState.Waiting || !selectedProperty;

    const trackedViewItemPropertyIdRef = useRef<number | null>(null);

    useEffect(() => {
        if (!selectedProperty?.id) {
            return;
        }

        quickViewPropertyChange(offer.id, selectedProperty.id);

        if (!amplitudeItemListContext?.itemListId || trackedViewItemPropertyIdRef.current === selectedProperty.id) {
            return;
        }

        trackedViewItemPropertyIdRef.current = selectedProperty.id;

        trackAmplitudeEvent("view_item", {
            ...getAmplitudeViewItemEventProperties({
                offer: selectedProperty.offer,
                property: {
                    id: selectedProperty.id,
                    number: selectedProperty.number,
                    area: selectedProperty.area,
                    rooms: selectedProperty.rooms,
                    floor: selectedProperty.floor,
                    price: selectedProperty.price
                },
                itemListId: amplitudeItemListContext?.itemListId,
                itemListName: amplitudeItemListContext?.itemListName
            }),
            location: "listing_map",
            index: selectedPropertyIndex
        });
    }, [selectedProperty, amplitudeItemListContext?.itemListId]);

    const updatePropertyDetailLinkWithPendingContext = (event: React.MouseEvent<HTMLElement>) => {
        const anchor = event.currentTarget as HTMLAnchorElement;

        anchor.href = createOfferListingAmplitudePendingDetailUrl({
            url: anchor.href,
            itemId: offer.id,
            propertyId: selectedProperty.id,
            itemListContext: amplitudeItemListContext
        });
    };

    const handleDetailsButtonClick = usePrimaryClick((event: React.MouseEvent<HTMLElement>) => {
        updatePropertyDetailLinkWithPendingContext(event);
        gtmClickPropertyMoreInfo();
        moreInfoLinkClick(offer.id, selectedProperty.id);
    });

    const handleGalleryButtonClick = (type: GalleryType) => {
        gtmClickGalleryButton(type);
        galleryButtonClick(type, offer.id, selectedProperty.id);

        trackAmplitudeOpenGallery({
            location: "listing_map",
            offerId: offer.id,
            propertyId: selectedProperty.id,
            galleryView: type === "photo" ? "grid" : "viewer",
            photoSection: type === "photo" ? "gallery" : "floor_plan"
        });
    };

    const nextClickAnalyticsEvent = () => {
        quickViewNextPropertyClick();
        trackAmplitudeButtonClick({
            buttonName: "rp_listing_map_apartment_next",
            buttonCategory: "listing_map",
            value: "next",
            itemId: offer.id,
            propertyId: selectedProperty.id
        });
    };

    const prevClickAnalyticsEvent = () => {
        quickViewPrevPropertyClick();
        trackAmplitudeButtonClick({
            buttonName: "rp_listing_map_apartment_prev",
            buttonCategory: "listing_map",
            value: "previous",
            itemId: offer.id,
            propertyId: selectedProperty.id
        });
    };

    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";

    const firstPlanPage = selectedProperty.plan_image_pages[0];
    const propertyPlanImage = getThumbnailLink(firstPlanPage?.image_url, 760, 428) ?? firstPlanPage?.image.p_img_760x428;
    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerId: offer.id,
        offerSlug: offer.slug,
        propertyId: selectedProperty.id
    });

    return (
        <div className={quickViewContainer}>
            {isLoading ? (
                <div className={topLoaderContainer}>
                    <Loader fullWidth size="lg" />
                </div>
            ) : (
                <>
                    <QWHeaderWithPagination
                        handleQuickViewClose={handleQuickViewClose}
                        setSelectedIndex={setSelectedIndex}
                        propertyList={props.propertyList}
                        selectedIndex={selectedIndex}
                        onNextClickEvent={nextClickAnalyticsEvent}
                        onPrevClickEvent={prevClickAnalyticsEvent}
                    />

                    <QWImageWithButton
                        alt={offer.name}
                        firstImageSrc={propertyPlanImage}
                        secondImageSrc={getThumbnailLink(offer.main_image_url, 760, 428) ?? offer.main_image.m_img_760x428}
                        clickEvent={handleGalleryButtonClick}
                        onPropertyLinkClick={updatePropertyDetailLinkWithPendingContext}
                        propertyLink={propertyLink}
                    />

                    <QWPropertyInfoHeading rooms={selectedProperty.rooms} offerType={offer.type} offerName={offer.name} />
                </>
            )}

            <div className={bottomContainerPadding}>
                {/*Those two components are showing offer info so they don't need to be recompiled every time when property change */}
                <QWLocalizationInfo address={offer.address} distanceFromRegion={offer.stats?.distance_from_region} offerType={offer.type} />

                {isLoading ? (
                    <Loader fullWidth size="lg" />
                ) : (
                    <>
                        <QWPictograms selectedProperty={selectedProperty} />

                        <QWActionButtons
                            vendorId={offer.vendor.id}
                            offerId={offer.id}
                            propertyId={selectedProperty.id}
                            propertyPrice={selectedProperty.price ?? null}
                            propertyLink={propertyLink}
                            applicationSource={ApplicationSource.QuickViewPropertyModal}
                            applicationSourceSection={ApplicationSourceSection.SIDEBAR}
                            clickEvent={handleDetailsButtonClick}
                            hideApplicationButton={isForSaleOverLimit}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

const bottomContainerPadding = css({
    pt: 0,
    px: 2,
    pb: 4
});

const quickViewContainer = css({
    display: "flex",
    flexDirection: "column",
    position: "absolute",
    zIndex: 1,
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: "var(--colors-gray-100)",
    padding: 0
});

const topLoaderContainer = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "normal",
    height: "51rem"
});
